import { kinds, ownerAccount, people, type MoneyDocument, type Owner } from "../../money/types";

export type RubOwnerFilter = Owner | "all";
export type RubHistoryRow = { title: string; reference: string; type: RubTypeFilter; id: string; document: MoneyDocument; owner: Owner; amount: number; at?: string };
export const operationTypes = [
  { value: "all", title: "Все операции" },
  { value: "expense", title: "Расходы" },
  { value: "sale", title: "Продажи" },
  { value: "capital", title: "Внесение и вывод" },
  { value: "transfer", title: "Переводы" },
  { value: "reconcile", title: "Сверки" },
] as const;
export type RubTypeFilter = typeof operationTypes[number]["value"];

function describe(document: MoneyDocument): { title: string; type: RubTypeFilter; reference: string } {
  const meta = document.metadata || {};
  const reference = meta.sku || meta.shipment_code || meta.products?.map(product => product.sku).join(", ") || "";
  const cashOut = meta.cash_out || meta.input?.cash_out || /вывод из бизнеса/i.test(document.description || "");
  const preorderTransfer = (meta as { preorder_transfer?: boolean }).preorder_transfer;
  switch (document.kind) {
    case "expense":
      return { title: cashOut ? "Вывод денег" : document.description || "Расход", type: cashOut ? "capital" : "expense", reference };
    case "product_expense":
      return { title: document.description || "Расход на сумку", type: "expense", reference };
    case "shipment_delivery":
    case "delivery":
      return { title: "Доставка поставки", type: "expense", reference };
    case "fee_reserve":
      return { title: document.description || "Комиссия", type: "expense", reference };
    case "sale":
      return { title: "Продажа сумки", type: "sale", reference };
    case "deposit":
      return { title: preorderTransfer ? "Из предзаказов" : "Внесение денег", type: preorderTransfer ? "transfer" : "capital", reference };
    case "exchange":
      return { title: document.target_currency === "USDT" ? "Пополнение цикла" : "Обмен валют", type: "transfer", reference };
    case "transfer":
      return { title: "Перевод", type: "transfer", reference };
    case "reconcile":
      return { title: "Сверка остатка", type: "reconcile", reference };
    default:
      return { title: kinds[document.kind] || "Операция", type: "all", reference };
  }
}
const owners: Owner[] = ["kolya", "vlad"];
const timestamp = (at?: string) => { const value = Date.parse(at || ""); return Number.isFinite(value) ? value : 0; };

/** Only posted ledger movements of the two RUB accounts; missing data is not an empty history. */
export function rubHistory(documents: MoneyDocument[], owner: RubOwnerFilter, query: string, type: RubTypeFilter = "all"): RubHistoryRow[] | null {
  const rows: RubHistoryRow[] = [], search = query.trim().toLowerCase();
  const byId = new Map(documents.map(document => [document.id, document]));
  for (const document of documents) {
    const original = document.reversed_document_id ? byId.get(document.reversed_document_id) : undefined;
    const detail = describe(original || document);
    const title = document.kind === "reversal" ? original ? `Отмена · ${detail.title}` : "Отмена операции" : detail.title;
    if (!Array.isArray(document.rub_movements)) return null;
    const seen = new Set<string>();
    for (const movement of document.rub_movements) {
      if (!movement || typeof movement.account_id !== "string") return null;
      const who = owners.find(candidate => ownerAccount(candidate) === movement.account_id);
      if (!who) continue;
      if (!Number.isSafeInteger(movement.amount_minor) || seen.has(movement.account_id)) return null;
      seen.add(movement.account_id);
      if (movement.amount_minor === 0 || (owner !== "all" && owner !== who)) continue;
      if (type !== "all" && detail.type !== type) continue;
      if (search && !`${title} ${detail.reference} ${document.id} ${document.description || ""} ${kinds[document.kind] || document.kind} ${people[who]}`.toLowerCase().includes(search)) continue;
      const at = document.occurred_at || document.posted_at || document.created_at;
      rows.push({ ...detail, title, id: `${document.id}:${who}`, document, owner: who, amount: movement.amount_minor, at: Number.isFinite(Date.parse(at || "")) ? at : undefined });
    }
  }
  return rows.sort((a, b) => timestamp(b.at) - timestamp(a.at) || a.document.id.localeCompare(b.document.id) || a.owner.localeCompare(b.owner));
}
